import React from "react"
import styled, { css } from "styled-components"
import { AppFunctionComponent } from "../../../../types"
import { Overlay, Box, Content } from "../modal.styled"
import { ModalProps } from "../modal.interface"
import CloseButton from "../../close-button/close-button.component"
import { Link } from "gatsby"

export type CustomButtonProps = {
  width?: string
  height?: string
  backgroundColor?: string
  top?: string
  bottom?: string
  borderRadius?: string
  padding?: string
  left?: string
  color?: string
  fontFamily?: string
  fontSize?: string
  fontWeight?: string
  lineHeight?: string
  textTransform?: string
  mobileWidth?: string
  mobileHeight?: string
  mobileTop?: string
  mobileFontSize?: string
}

const ContentBox = styled(Content)<{
  backgroundImage?: string
  mobileBackgroundImage?: string
  windowWebStyle?: {
    width?: string
    height?: string
  }
  windowMobileStyle?: {
    width?: string
    height?: string
  }
}>`
  position: relative;
  background: ${({ theme, mobileBackgroundImage }) =>
    "center " + "no-repeat " + "url(" + mobileBackgroundImage + ")" ||
    theme.color.backgroundInverted};
  box-shadow: 0 2px 20px 0 ${({ theme }) => theme.color.lightShadow};
  padding: 50px 30px 30px;
  background-size: contain;

  ${({ windowMobileStyle }) =>
    windowMobileStyle &&
    css`
      width: ${`${windowMobileStyle.width}` || "unset"};
      height: ${`${windowMobileStyle.height}` || "unset"};
    `}

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 50px;
    background: ${({ theme, backgroundImage }) =>
      "center " + "no-repeat " + "url(" + backgroundImage + ")" ||
      theme.color.backgroundInverted};
    background-size: contain;
    ${({ windowWebStyle }) =>
      windowWebStyle &&
      css`
        width: ${`${windowWebStyle.width}` || "unset"};
        height: ${`${windowWebStyle.height}` || "unset"};
      `}
  }
`

const CustomButton = styled.button<{
  mobile?: CustomButtonProps
  desktop?: CustomButtonProps
}>`
  position: absolute;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ mobile }) => `${mobile?.width}` || "unset"};
  height: ${({ mobile }) => `${mobile?.height}` || "unset"};
  top: ${({ mobile }) => `${mobile?.top}` || "unset"};
  left: ${({ mobile }) => `${mobile?.left ?? mobile?.left}` || "unset"};
  background-color: ${({ desktop }) =>
    `${desktop?.backgroundColor}` || "unset"};
  border-radius: ${({ desktop }) => desktop?.borderRadius || "unset"};
  padding: ${({ desktop }) => `${desktop?.padding}px` || "unset"};
  color: ${({ desktop }) => desktop?.color || "unset"};
  font-weight: ${({ desktop }) => desktop?.fontWeight || "unset"};
  font-size: ${({ mobile }) => mobile?.fontSize || "unset"};
  font-family: ${({ desktop }) => desktop?.fontFamily || "unset"};
  line-height: ${({ desktop }) => desktop?.lineHeight || "unset"};
  text-transform: ${({ desktop }) => desktop?.textTransform || "unset"};

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    width: ${({ desktop }) => desktop?.width || "unset"};
    height: ${({ desktop }) => desktop?.height || "unset"};
    top: ${({ desktop }) => desktop?.top || "unset"};
    bottom: ${({ desktop }) => desktop?.bottom || "unset"};
    font-size: ${({ desktop }) => desktop?.fontSize || "unset"};
    left: ${({ desktop }) => desktop?.left};
    transform: translateX(-${({ desktop }) => desktop?.left});
  }
`

const Close = styled(CloseButton)`
  --edge-space: 20px;

  position: absolute;
  top: var(--edge-space);
  right: var(--edge-space);
`

export type PopupProps = {
  desktopImage?: string
  mobileImage?: string
  buttonLink?: string
  secondButtonLink?: string
  buttonText?: string
  buttonWebStyle?: CustomButtonProps
  buttonMobileStyle?: CustomButtonProps
  closeButtonWebStyle?: CustomButtonProps
  closeButtonMobileStyle?: CustomButtonProps
  windowWebStyle?: {
    width: string
    height: string
  }
  windowMobileStyle?: {
    width: string
    height: string
  }
}

const Popup: AppFunctionComponent<ModalProps & PopupProps> = ({
  onClose,
  desktopImage,
  mobileImage,
  buttonLink,
  buttonText,
  buttonWebStyle,
  buttonMobileStyle,
  closeButtonWebStyle,
  closeButtonMobileStyle,
  windowMobileStyle,
  windowWebStyle,
  secondButtonLink,
}) => {
  return (
    <>
      <Overlay onClick={onClose} data-testid="modal-overlay" />
      <Box>
        <ContentBox
          data-testid="modal-content-wrapper"
          backgroundImage={desktopImage}
          mobileBackgroundImage={mobileImage}
          windowMobileStyle={windowMobileStyle}
          windowWebStyle={windowWebStyle}
        >
          <Close onClick={onClose} />
          {buttonLink && (
            <>
              <Link to={buttonLink}>
                <CustomButton
                  desktop={buttonWebStyle}
                  mobile={buttonMobileStyle}
                  aria-label="Proceed"
                >
                  {buttonText}
                </CustomButton>
              </Link>
              {secondButtonLink ? (
                <Link to={secondButtonLink}>
                  <CustomButton
                    onClick={onClose}
                    desktop={closeButtonWebStyle}
                    mobile={closeButtonMobileStyle}
                    aria-label="Cancel"
                  ></CustomButton>
                </Link>
              ) : (
                <CustomButton
                  onClick={onClose}
                  desktop={closeButtonWebStyle}
                  mobile={closeButtonMobileStyle}
                  aria-label="Cancel"
                ></CustomButton>
              )}
            </>
          )}
        </ContentBox>
      </Box>
    </>
  )
}

export default Popup
